/**
 * DietaryChipPicker (Plan 11, Task 3).
 *
 * Same visual pattern as AllergyChipPicker but NO confirmation checkbox
 * (UI-SPEC §5: dietary preferences are not safety-critical).
 */
import { useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { DIETARY_CHIPS, type DietaryChip } from '@stell/shared';
import { Button } from '../../../components/primitives/Button';
import { Chip, chipClass } from '../../../components/primitives/Chip';
import { SectionLabel } from '../../../components/primitives/SectionLabel';

const GLYPH_REMOVE = '×';
const GLYPH_ADD = '+';

export interface DietaryChipPickerValue {
  chips: DietaryChip[];
  custom: string[];
}

export interface DietaryChipPickerProps {
  value: DietaryChipPickerValue;
  onChange: (next: DietaryChipPickerValue) => void;
  /** Tekst i feltet som ikke er lagt til med «+» ennå (#166), som i AllergyChipPicker. */
  pendingText?: string;
  onPendingTextChange?: (next: string) => void;
}

export function DietaryChipPicker({
  value,
  onChange,
  pendingText,
  onPendingTextChange,
}: DietaryChipPickerProps) {
  const { t } = useTranslation();
  const [ownDraft, setOwnDraft] = useState('');
  const draft = pendingText ?? ownDraft;
  const setDraft = onPendingTextChange ?? setOwnDraft;
  // Tekst som ikke er lagt til, blir ikke lagret. Oppsett sperrer derfor
  // «Lagre medlem» mens den står der (#166), og her sies det hvorfor.
  const pending = draft.trim();

  function toggleChip(chip: DietaryChip) {
    const has = value.chips.includes(chip);
    onChange({
      chips: has ? value.chips.filter((c) => c !== chip) : [...value.chips, chip],
      custom: value.custom,
    });
  }

  function addCustom() {
    const cleaned = draft.trim();
    if (!cleaned) return;
    if (value.custom.includes(cleaned)) {
      setDraft('');
      return;
    }
    onChange({ chips: value.chips, custom: [...value.custom, cleaned] });
    setDraft('');
  }

  function removeCustom(item: string) {
    onChange({
      chips: value.chips,
      custom: value.custom.filter((c) => c !== item),
    });
  }

  function onDraftKey(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault();
      addCustom();
    }
  }

  // #159 steg 9f: vanlige chips (valgt = `bg-solid`), så kosthold ikke kan
  // forveksles med de amber allergichipene rett over.
  return (
    <fieldset data-testid="dietary-picker" className="m-0 flex flex-col gap-2 border-0 p-0">
      <SectionLabel as="legend">{t('onboarding.step3.dietary')}</SectionLabel>

      <div className="flex flex-wrap gap-2">
        {DIETARY_CHIPS.map((chip) => (
          <Chip
            key={chip}
            selected={value.chips.includes(chip)}
            data-testid={`dietary-chip-${chip}`}
            onClick={() => toggleChip(chip)}
          >
            {t(`dietary.chip.${chip}`)}
          </Chip>
        ))}
        {value.custom.map((item) => (
          <span
            key={item}
            data-testid={`dietary-custom-${item}`}
            className={`${chipClass(true)} pl-3.5`}
          >
            {item}
            <button
              type="button"
              data-testid={`dietary-custom-remove-${item}`}
              aria-label={t('common.remove_item', { item })}
              onClick={() => removeCustom(item)}
              className="inline-flex h-11 w-11 items-center justify-center rounded-pill text-base"
            >
              {GLYPH_REMOVE}
            </button>
          </span>
        ))}
      </div>

      <div className="flex gap-2">
        <input
          data-testid="dietary-custom-input"
          className="field min-w-0 flex-1"
          type="text"
          maxLength={40}
          aria-label={t('onboarding.step3.dietary')}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onDraftKey}
        />
        <Button
          variant="secondary"
          size={52}
          data-testid="dietary-custom-add"
          aria-label={t('common.add')}
          className="w-13 shrink-0 px-0 text-lg"
          onClick={addCustom}
        >
          {GLYPH_ADD}
        </Button>
      </div>
      {pending !== '' ? (
        <p data-testid="dietary-pending" className="m-0 text-sm text-ink-2">
          {t('onboarding.step3.dietary_pending', { item: pending })}
        </p>
      ) : null}
    </fieldset>
  );
}
